/**
 * When a booking starts and ends, as the assistant reads it.
 *
 * Bookings are sold in 12-hour blocks that can begin at any half-hour, so the
 * date alone never says when to turn up: two bookings on the same day can be
 * twelve hours apart. Rendered in Africa/Lagos — the zone the server uses for
 * the emails it sends assistants (ASSISTANT_TIME_ZONE in pulo_api) — so one
 * booking cannot read as two different times in an inbox and on the dashboard.
 */
export const BOOKING_TIME_ZONE = "Africa/Lagos";
export const BOOKING_BLOCK_HOURS = 12;

const MS_PER_HOUR = 60 * 60 * 1000;

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const format = (date, options) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: BOOKING_TIME_ZONE, ...options }).format(date);

/** e.g. "24 Sept 2026, 10:30 am". */
export const formatBookingDateTime = (value) => {
  const date = parseDate(value);
  if (!date) return "—";
  return format(date, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/** e.g. "24 Sept 2026", for places with no room for the time. */
export const formatBookingDate = (value) => {
  const date = parseDate(value);
  if (!date) return "—";
  return format(date, { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * How long the assistant is booked for: "24 hours (2 blocks)".
 *
 * `hours` and `blocks` are snapshotted on the booking, but rows written before
 * block pricing have neither — measure the window itself for those.
 */
export const formatBookingDuration = (booking = {}) => {
  const start = parseDate(booking.arrivalDate);
  const end = parseDate(booking.departureDate);
  const windowHours = start && end ? (end.getTime() - start.getTime()) / MS_PER_HOUR : 0;
  const blocksHours = Number(booking.blocks) * BOOKING_BLOCK_HOURS;
  const hours = Number(booking.hours) || blocksHours || windowHours;
  if (!Number.isFinite(hours) || hours <= 0) return "—";

  const rounded = Math.round(hours * 100) / 100;
  const hoursLabel = `${rounded} hour${rounded === 1 ? "" : "s"}`;
  const blocks = rounded / BOOKING_BLOCK_HOURS;
  return Number.isInteger(blocks)
    ? `${hoursLabel} (${blocks} block${blocks === 1 ? "" : "s"})`
    : hoursLabel;
};
